/**
 * The libraries' own words in the interface's language (docs/plans/INTERFACE_LANGUAGES_PLAN.md §4.2),
 * set in one place from the language system-language.ts loaded:
 * - PrimeNG (empty and filter messages, date words, about 60 screen-reader labels): primelocale's
 *   catalogue for the language, loaded with it and handed to providePrimeNG (app.module);
 * - Material's datepicker: its labels through our own catalogue ($localize, DurveyDatepickerIntl);
 *   its dates follow LOCALE_ID through the native date adapter.
 * reCAPTCHA, Stripe Checkout, Plotly, Chart.js and Quill follow with their areas (plan S13).
 */
import { Injectable } from '@angular/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import type { Translation } from 'primeng/api';

/** primelocale's file per registry code, one chunk each, loaded only for the language in use. */
const PRIMENG: Readonly<Record<string, () => Promise<Translation>>> = {
  de: () => import('primelocale/de.json').then(m => m.default.de),
  fr: () => import('primelocale/fr.json').then(m => m.default.fr),
  es: () => import('primelocale/es.json').then(m => m.default.es),
  it: () => import('primelocale/it.json').then(m => m.default.it),
  'pt-PT': () => import('primelocale/pt.json').then(m => m.default.pt),
  'pt-BR': () => import('primelocale/pt-BR.json').then(m => m.default['pt-BR']),
  nl: () => import('primelocale/nl.json').then(m => m.default.nl),
  pl: () => import('primelocale/pl.json').then(m => m.default.pl),
  sv: () => import('primelocale/sv.json').then(m => m.default.sv),
  da: () => import('primelocale/da.json').then(m => m.default.da),
  nb: () => import('primelocale/nb-NO.json').then(m => m.default['nb-NO']),
  fi: () => import('primelocale/fi.json').then(m => m.default.fi),
  cs: () => import('primelocale/cs.json').then(m => m.default.cs),
  el: () => import('primelocale/el.json').then(m => m.default.el),
  tr: () => import('primelocale/tr.json').then(m => m.default.tr),
};

let primeNg: Translation | undefined;

/** Load the libraries' words for `code`; English (or one that fails) leaves PrimeNG's own English. */
export async function loadLibraryTexts(code: string): Promise<void> {
  const load = PRIMENG[code];
  primeNg = load ? await load().catch(() => undefined) : undefined;
}

/** PrimeNG's words in the interface's language, for providePrimeNG; undefined in English. */
export function primeNgTranslation(): Translation | undefined {
  return primeNg;
}

/** The languages primelocale is wired for (a spec holds it to the interface's languages). */
export function primeNgLanguages(): string[] {
  return Object.keys(PRIMENG);
}

/** Material's datepicker labels, as our own messages. */
@Injectable()
export class DurveyDatepickerIntl extends MatDatepickerIntl {
  override calendarLabel = $localize`:datepicker, screen-reader label:Calendar`;
  override openCalendarLabel = $localize`:datepicker, screen-reader label:Open calendar`;
  override closeCalendarLabel = $localize`:datepicker, screen-reader label:Close calendar`;
  override prevMonthLabel = $localize`:datepicker, screen-reader label:Previous month`;
  override nextMonthLabel = $localize`:datepicker, screen-reader label:Next month`;
  override prevYearLabel = $localize`:datepicker, screen-reader label:Previous year`;
  override nextYearLabel = $localize`:datepicker, screen-reader label:Next year`;
  override prevMultiYearLabel = $localize`:datepicker, screen-reader label:Previous 24 years`;
  override nextMultiYearLabel = $localize`:datepicker, screen-reader label:Next 24 years`;
  override switchToMonthViewLabel = $localize`:datepicker, screen-reader label:Choose date`;
  override switchToMultiYearViewLabel = $localize`:datepicker, screen-reader label:Choose month and year`;
  override startDateLabel = $localize`:datepicker, screen-reader label:Start date`;
  override endDateLabel = $localize`:datepicker, screen-reader label:End date`;
  override comparisonDateLabel = $localize`:datepicker, screen-reader label:Comparison range`;

  override formatYearRangeLabel(start: string, end: string): string {
    return $localize`:datepicker, screen-reader label for a range of years:${start}:start: to ${end}:end:`;
  }
}
